import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Play, Clock, BarChart3, FileText, Download,
  ChevronDown, ChevronRight, Users, BookOpen, GraduationCap,
  X, Info, Gamepad2, Eye, Lock,
} from 'lucide-react';
import axios from 'axios';
import DayScopePanel from '../components/DayScopePanel';
import { getScopeCards, scopeMatchesBlock } from '../data/scopeLookup';

/* Does a scope card carry anything worth showing in the panel? */
function hasScopeContent(card) {
  if (!card) return false;
  const cur = card.curriculum || {};
  return !!(
    card.can_do ||
    (card.recognize || []).length ||
    (card.produce || []).length ||
    (card.culture && card.culture.anchor) ||
    (cur.rows || []).length ||
    cur.cefr
  );
}

const GAME_ICONS = {
  la_course: { emoji: '\u{1F3C6}', name: 'La Course' },
  duel_conjugaison: { emoji: '\u{2694}\u{FE0F}', name: 'Le Duel' },
  la_tour: { emoji: '\u{1F3D7}\u{FE0F}', name: 'La Tour' },
  horloge_folle: { emoji: '\u{1F550}', name: "L'Horloge Folle" },
  avalanche: { emoji: '\u{1F3D4}\u{FE0F}', name: "L'Avalanche" },
  memoire: { emoji: '\u{1F3B4}', name: 'La Memoire' },
  tri_express: { emoji: '\u{1F682}', name: 'Tri Express' },
  batisseur: { emoji: '\u{1F9F1}', name: 'Batisseur' },
  robot_balayeur: { emoji: '\u{1F916}', name: 'Robot Balayeur' },
  detective: { emoji: '\u{1F50D}', name: 'Le Detective' },
  carte_du_canada: { emoji: '\u{1F5FA}\u{FE0F}', name: 'Carte du Canada' },
  le_chef: { emoji: '\u{1F468}\u{200D}\u{1F373}', name: 'Le Chef' },
};

const CEFR_COLORS = {
  'pre-A1': '#2F9E44',
  'A1': '#1971C2',
  'A1-A2': '#F59F00',
  'A2': '#E8590C',
};

/* ===================================================================
 * Normalize blocks: handle both block-based and flat session formats
 *   - block-based programs (P09/P11): session.blocks[]
 *   - flat session (P12): session.title + lesson_id + game_id on session itself
 *   - flat session (P13): session.title_fr/en + nullable lesson_id/game_id
 *   - flat session (P14 inner): session.label + lesson_id + game_id + strands + type
 *   - inline sessions (P05/P06/P08/P10): session.session_title_fr/en + lesson_id + game_id
 * =================================================================== */
function getBlocksForSession(session) {
  // Block-based programs: session.blocks[] has content
  if (session.blocks && session.blocks.length > 0) {
    return session.blocks;
  }
  // Flat-format programs: content is directly on the session object
  const hasAnyContent =
    session.game_id || session.lesson_id ||
    session.title || session.title_fr || session.title_en ||
    session.session_title_fr || session.session_title_en ||
    session.label || session.block_label;
  if (hasAnyContent) {
    return [{
      block_label:
        session.block_label || session.label ||
        session.title_en || session.session_title_en || session.title ||
        session.title_fr || session.session_title_fr || session.day_label || '',
      block_label_fr:
        session.title_fr || session.session_title_fr || session.label ||
        session.block_label || session.title || '',
      lesson_id: session.lesson_id || null,
      game_id: session.game_id || null,
      preset_config: session.preset_config || null,
      duration_minutes: session.duration_minutes || null,
      facilitation_note: session.facilitation_note || session.facilitation_note_fr || null,
      facilitation_note_fr: session.facilitation_note_fr || session.facilitation_note || null,
      facilitation_note_en: session.facilitation_note_en || null,
      session_type: session.session_type || session.type || null,
      focus: session.focus || null,
      strands: session.strands || null,
      weekly_phase: session.weekly_phase || null,
    }];
  }
  return [];
}

function getSessionDayLabel(session) {
  const num = session.session_number || session.session;
  return session.day || session.day_label || session.day_date || (num ? `Session ${num}` : '');
}

/* ===================================================================
 * Flatten session_sequence into a single list of weeks for rendering.
 * Some programs (P14) wrap weeks inside outer "blocks" via .weeks_detail —
 * we flatten that layer here and attach a _sectionLabel so the renderer
 * can show a section divider above the relevant weeks. Other programs
 * pass through unchanged.
 * =================================================================== */
function flattenWeeks(sessionSequence) {
  const out = [];
  for (const entry of sessionSequence || []) {
    if (Array.isArray(entry.weeks_detail) && entry.weeks_detail.length > 0) {
      // P14 shape: { block, block_label_fr/en, weeks, sessions_range, theme, weeks_detail[] }
      entry.weeks_detail.forEach((wk, i) => {
        out.push({
          ...wk,
          _sectionLabel: entry.block_label_en || entry.block_label || '',
          _sectionLabelFr: entry.block_label_fr || entry.block_label_en || '',
          _sectionTheme: entry.theme || '',
          _sectionFirst: i === 0,
        });
      });
    } else {
      out.push(entry);
    }
  }
  return out;
}

/* Pretty-print a snake_case enum like "grammar_deep_dive" → "Grammar deep dive" */
function prettyPhase(s) {
  if (!s) return '';
  return s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
}

export function ProgramDetail() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [activeTab, setActiveTab] = useState('schedule');
  const [scopeCards, setScopeCards] = useState([]);
  const [scopeCard, setScopeCard] = useState(null);
  const isFr = lang === 'fr';
  const flatWeeks = useMemo(() => flattenWeeks(program?.session_sequence), [program]);
  // Resources tab is hidden entirely when the user's master Resources switch is off.
  // Default true preserves legacy behaviour for any account where the field is unset.
  const resourcesEnabled = user?.resources_enabled !== false;
  // Demo guests enter via an invite link with no login. They can browse the
  // Resources list (as a teaser) but cannot open the documents, those open only
  // on a fully provisioned account.
  const isDemoGuest = !!user?.demo || user?.role === 'invite_lead';

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/programs/${programId}`),
      axios.get(`${API}/programs/${programId}/resources`),
    ])
      .then(([progRes, resRes]) => {
        setProgram(progRes.data);
        setResources(resRes.data);
        if (progRes.data.session_sequence?.length > 0) {
          setExpandedWeeks({ 0: true });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [programId]);

  // Load the additive day-scope cards for this program (no-op if no scope file exists).
  useEffect(() => {
    let active = true;
    setScopeCards([]);
    getScopeCards(programId).then((cards) => { if (active) setScopeCards(cards); });
    return () => { active = false; };
  }, [programId]);

  const toggleWeek = (idx) => {
    setExpandedWeeks(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  if (loading || !program) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <p style={{ color: '#64748B' }}>{isFr ? 'Chargement...' : 'Loading...'}</p>
      </div>
    );
  }

  return (
    <div data-testid="program-detail" style={{ height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <div style={{
        padding: '1.5rem 2rem', borderBottom: '1px solid #E2E8F0',
        background: `linear-gradient(135deg, ${program.color || '#1971C2'}08, white)`,
      }}>
        <button
          data-testid="program-back-btn"
          onClick={() => navigate('/programs')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            color: '#64748B', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Nunito, sans-serif',
            padding: 0, marginBottom: '0.75rem',
          }}
        >
          <ArrowLeft size={16} /> {isFr ? 'Tous les programmes' : 'All Programs'}
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: (program.color || '#1971C2') + '15',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <GraduationCap size={24} color={program.color || '#1971C2'} />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.3rem, 2.5vw, 1.7rem)',
              fontWeight: 700, color: '#1A1A2E', margin: 0, lineHeight: 1.2,
            }}>
              {isFr ? (program.title_fr || program.title) : program.title}
            </h1>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '0.78rem', fontWeight: 600,
                color: program.color || '#1971C2',
                background: (program.color || '#1971C2') + '12',
                borderRadius: 6, padding: '3px 10px',
              }}>
                {program.cefr_start} → {program.cefr_end}
              </span>
              <span style={{ fontSize: '0.78rem', color: '#64748B', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Clock size={13} /> {program.total_sessions} {isFr ? 'seances' : 'sessions'}
              </span>
              <span style={{ fontSize: '0.78rem', color: '#64748B', display: 'flex', alignItems: 'center', gap: 4 }}>
                <BarChart3 size={13} /> {flatWeeks.length} {isFr ? 'semaines' : 'weeks'}
              </span>
            </div>
          </div>
        </div>

        <p style={{
          color: '#475569', fontSize: '0.88rem', marginTop: '0.75rem',
          lineHeight: 1.6, fontFamily: 'Nunito, sans-serif', maxWidth: 700,
        }}>
          {program.description}
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: '0.25rem', padding: '0.5rem 2rem 0', borderBottom: '1px solid #E2E8F0',
      }}>
        {[
          { key: 'schedule', labelEn: 'Schedule', labelFr: 'Horaire', icon: Clock },
          { key: 'resources', labelEn: 'Resources', labelFr: 'Ressources', icon: FileText },
        ].filter(t => t.key !== 'resources' || resourcesEnabled).map(tab => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              data-testid={`program-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '0.6rem 1rem', border: 'none', cursor: 'pointer',
                background: 'none', fontFamily: 'Nunito, sans-serif', fontSize: '0.88rem',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? program.color || '#1971C2' : '#64748B',
                borderBottom: isActive ? `2px solid ${program.color || '#1971C2'}` : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              <TabIcon size={15} /> {isFr ? tab.labelFr : tab.labelEn}
              {tab.key === 'resources' && <span style={{
                fontSize: '0.72rem', background: '#F1F5F9', borderRadius: 8, padding: '1px 6px',
              }}>{resources.length}</span>}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div style={{ padding: '1.5rem 2rem' }}>
        {activeTab === 'schedule' && (
          <ScheduleTab
            program={program}
            weeks={flatWeeks}
            expandedWeeks={expandedWeeks}
            toggleWeek={toggleWeek}
            navigate={navigate}
            isFr={isFr}
            scopeCards={scopeCards}
            onOpenScope={setScopeCard}
          />
        )}
        {activeTab === 'resources' && (
          <ResourcesTab
            program={program}
            resources={resources}
            isFr={isFr}
            locked={isDemoGuest}
          />
        )}
      </div>

      {/* Additive day-scope pop-up (opens from the Scope button on a schedule day) */}
      {scopeCard && (
        <DayScopePanel
          card={scopeCard}
          accent={program.color || '#1971C2'}
          isFr={isFr}
          onClose={() => setScopeCard(null)}
          programId={program.program_id}
          programTitle={isFr ? (program.title_fr || program.title) : program.title}
          audienceType={program.audience_type}
          isDemo={isDemoGuest}
        />
      )}
    </div>
  );
}

/* ===================================================================
 * SCHEDULE TAB — handles every program shape (block-based, flat session,
 * outer-block-with-weeks_detail). Renders section dividers when a
 * program groups weeks under a higher-level label (phase or block).
 * =================================================================== */
function ScheduleTab({ program, weeks, expandedWeeks, toggleWeek, navigate, isFr, scopeCards = [], onOpenScope }) {
  let prevSection = null;

  // Map each rendered block (week→session→block order) to its scope card index.
  // This mirrors the exact render order below and is independent of expand state,
  // so cards stay aligned even when only some weeks are open.
  const cardIndexByKey = useMemo(() => {
    const map = {};
    let counter = 0;
    (weeks || []).forEach((week, wIdx) => {
      (week.sessions || []).forEach((session, sIdx) => {
        getBlocksForSession(session).forEach((block, bIdx) => {
          map[`${wIdx}-${sIdx}-${bIdx}`] = counter;
          counter += 1;
        });
      });
    });
    return map;
  }, [weeks]);

  return (
    <div data-testid="program-schedule">
      {weeks.map((week, idx) => {
        const isExpanded = expandedWeeks[idx];
        // Section label can come from either P13's phase_label or P14's _sectionLabel
        const sectionLabel = week.phase_label || (isFr ? week._sectionLabelFr : week._sectionLabel) || null;
        const sectionTheme = week._sectionTheme || null;
        const showSectionHeader = sectionLabel && sectionLabel !== prevSection;
        if (sectionLabel) prevSection = sectionLabel;

        return (
          <React.Fragment key={idx}>
            {showSectionHeader && (
              <div
                data-testid={`section-divider-${idx}`}
                style={{
                  marginTop: idx === 0 ? 0 : '1.25rem', marginBottom: '0.5rem',
                  padding: '0.5rem 0.85rem', borderLeft: `3px solid ${program.color || '#1971C2'}`,
                  background: (program.color || '#1971C2') + '08',
                  borderRadius: '0 8px 8px 0',
                }}
              >
                <div style={{
                  fontFamily: 'Outfit, sans-serif', fontSize: '0.78rem', fontWeight: 700,
                  color: program.color || '#1971C2', letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}>
                  {sectionLabel}
                </div>
                {sectionTheme && (
                  <div style={{ marginTop: 3, fontSize: '0.78rem', color: '#475569', lineHeight: 1.4 }}>
                    {sectionTheme}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: '0.5rem' }}>
              {/* Week Header */}
              <button
                data-testid={`week-toggle-${idx}`}
                onClick={() => toggleWeek(idx)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem 1rem', background: isExpanded ? '#F8FAFC' : 'white',
                  border: '1px solid #E2E8F0', borderRadius: isExpanded ? '10px 10px 0 0' : 10,
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: (program.color || '#1971C2') + '15',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 700, color: program.color || '#1971C2',
                }}>
                  {week.week || idx + 1}
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <span style={{
                    fontFamily: 'Outfit, sans-serif', fontSize: '0.92rem', fontWeight: 600, color: '#1A1A2E',
                  }}>
                    {isFr
                      ? (week.week_title_fr || week.week_title_en || week.block_title_fr || `Semaine ${week.week || idx + 1}`)
                      : (week.week_title_en || week.block_title_en || `Week ${week.week || idx + 1}`)}
                  </span>
                  <span style={{ fontSize: '0.78rem', color: '#94A3B8', marginLeft: 8 }}>
                    {week.sessions?.length || 0} {isFr ? 'jours' : 'days'}
                  </span>
                  {week.week_dates && (
                    <div style={{ fontSize: '0.74rem', color: '#94A3B8', marginTop: 2 }}>
                      {week.week_dates}
                    </div>
                  )}
                  {week.milestone && (
                    <div style={{ fontSize: '0.76rem', color: '#475569', marginTop: 3, fontStyle: 'italic' }}>
                      {isFr ? 'Objectif' : 'Milestone'}: {week.milestone}
                    </div>
                  )}
                </div>
                {isExpanded ? <ChevronDown size={18} color="#94A3B8" /> : <ChevronRight size={18} color="#94A3B8" />}
              </button>

              {/* Week Content */}
              {isExpanded && (
                <div style={{
                  border: '1px solid #E2E8F0', borderTop: 'none',
                  borderRadius: '0 0 10px 10px', overflow: 'hidden',
                }}>
                  {week.week_note && (
                    <div style={{
                      padding: '0.55rem 1rem', background: '#FFFBEB',
                      borderBottom: '1px solid #FEF3C7',
                      fontSize: '0.78rem', color: '#92400E', lineHeight: 1.5,
                    }}>
                      <Info size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
                      {week.week_note}
                    </div>
                  )}
                  {(week.sessions || []).map((session, sIdx) => {
                    const blocks = getBlocksForSession(session);
                    const dayLabel = getSessionDayLabel(session);
                    const phasePill = session.weekly_phase || null;

                    return (
                      <div key={sIdx}>
                        {/* Day Header */}
                        <div style={{
                          padding: '0.5rem 1rem', background: '#F8FAFC',
                          fontSize: '0.78rem', fontWeight: 600, color: '#64748B',
                          borderBottom: '1px solid #F1F5F9',
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <span>{dayLabel}</span>
                          {phasePill && (
                            <span style={{
                              fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.03em',
                              textTransform: 'uppercase',
                              color: program.color || '#1971C2',
                              background: (program.color || '#1971C2') + '14',
                              borderRadius: 4, padding: '1px 6px',
                            }}>
                              {prettyPhase(phasePill)}
                            </span>
                          )}
                        </div>
                        {/* Blocks */}
                        {blocks.map((block, bIdx) => {
                          const cardIdx = cardIndexByKey[`${idx}-${sIdx}-${bIdx}`];
                          const rawCard = (cardIdx != null) ? scopeCards[cardIdx] : null;
                          const card = (rawCard && scopeMatchesBlock(rawCard, block) && hasScopeContent(rawCard)) ? rawCard : null;
                          return (
                            <SessionBlock
                              key={bIdx}
                              block={block}
                              card={card}
                              onOpenScope={onOpenScope}
                              program={program}
                              navigate={navigate}
                              isFr={isFr}
                              testId={`session-block-${idx}-${sIdx}-${bIdx}`}
                            />
                          );
                        })}
                        {blocks.length === 0 && (
                          <div style={{
                            padding: '0.65rem 1rem',
                            fontSize: '0.82rem', color: '#94A3B8', fontStyle: 'italic',
                          }}>
                            {isFr ? 'Aucun contenu pour cette session' : 'No content for this session'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* A single session block — works for both linked (lesson_id) and inline blocks */
function SessionBlock({ block, program, navigate, isFr, testId, card, onOpenScope }) {
  const gameInfo = GAME_ICONS[block.game_id] || null;
  const cefrColor = CEFR_COLORS[block.preset_config?.level] || '#64748B';
  const hasLesson = !!block.lesson_id;
  const hasGame = !!block.game_id;
  const isClickable = hasLesson || hasGame;

  const label = isFr
    ? (block.block_label_fr || block.block_label || gameInfo?.name || '')
    : (block.block_label || gameInfo?.name || '');

  const handleClick = () => {
    if (hasLesson) {
      navigate(`/programs/${program.program_id}/lessons/${block.lesson_id}`);
    } else if (hasGame) {
      // Inline block — go directly to game setup with preset config
      const pc = block.preset_config || {};
      const params = new URLSearchParams();
      if (pc.mode) params.set('mode', pc.mode);
      if (pc.level) params.set('level', pc.level);
      if (pc.topic) params.set('topic', pc.topic);
      if (pc.difficulty) params.set('difficulty', pc.difficulty);
      if (pc.item_id) params.set('item_id', pc.item_id);
      // Return to this program when the game is exited (instead of /browse).
      params.set('returnTo', `/programs/${program.program_id}`);
      navigate(`/play/${block.game_id}/setup?${params.toString()}`);
    }
  };

  return (
    <div
      data-testid={testId}
      onClick={isClickable ? handleClick : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.65rem 1rem', borderBottom: '1px solid #F1F5F9',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { if (isClickable) e.currentTarget.style.background = '#F8FAFC'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
    >
      <span style={{ fontSize: '1.2rem', width: 28, textAlign: 'center' }}>
        {gameInfo ? gameInfo.emoji : (block.session_type === 'diagnostic' ? '\u{1F4CB}' : '\u{1F4DA}')}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1A1A2E' }}>
          {label}
        </div>
        {block.focus && (
          <div style={{ fontSize: '0.76rem', color: '#64748B', marginTop: 2, lineHeight: 1.4 }}>
            {block.focus}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: 2, flexWrap: 'wrap' }}>
          {block.preset_config?.level && (
            <span style={{
              fontSize: '0.7rem', color: cefrColor, fontWeight: 600,
              background: cefrColor + '12', borderRadius: 4, padding: '1px 6px',
            }}>
              {block.preset_config.level}
            </span>
          )}
          {block.preset_config?.topic && (
            <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
              {block.preset_config.topic.replace(/_/g, ' ')}
            </span>
          )}
          {block.duration_minutes && (
            <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
              {block.duration_minutes} min
            </span>
          )}
          {Array.isArray(block.strands) && block.strands.length > 0 && (
            <span style={{
              fontSize: '0.68rem', color: '#64748B', fontWeight: 600,
              background: '#F1F5F9', borderRadius: 4, padding: '1px 6px',
              letterSpacing: '0.05em',
            }}>
              {block.strands.join(' / ')}
            </span>
          )}
          {block.session_type && !hasLesson && (
            <span style={{
              fontSize: '0.68rem', color: '#64748B', fontWeight: 600,
              background: '#F1F5F9', borderRadius: 4, padding: '1px 6px',
              textTransform: 'capitalize',
            }}>
              {block.session_type}
            </span>
          )}
        </div>
      </div>
      {card && onOpenScope && (
        <button
          data-testid={`${testId}-scope-btn`}
          onClick={(e) => { e.stopPropagation(); onOpenScope(card); }}
          title={isFr ? 'Voir le contenu de la journée' : "See this day's scope"}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 9px', borderRadius: 6, flexShrink: 0,
            border: `1px solid ${(program.color || '#1971C2')}33`,
            background: (program.color || '#1971C2') + '0D',
            color: program.color || '#1971C2',
            fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
            fontFamily: 'Nunito, sans-serif',
            transition: 'background 0.12s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = (program.color || '#1971C2') + '1A'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = (program.color || '#1971C2') + '0D'; }}
        >
          <Eye size={12} /> {isFr ? 'Aperçu' : 'Scope'}
        </button>
      )}
      {hasLesson && <Play size={16} color={program.color || '#1971C2'} style={{ opacity: 0.6 }} />}
      {!hasLesson && hasGame && <Gamepad2 size={16} color={program.color || '#1971C2'} style={{ opacity: 0.5 }} />}
    </div>
  );
}

/* ===================================================================
 * RESOURCES TAB — inline markdown rendering instead of raw text links
 * =================================================================== */
function ResourcesTab({ program, resources, isFr, locked = false }) {
  const [selectedResource, setSelectedResource] = useState(null);
  const [resourceContent, setResourceContent] = useState('');
  const [loadingResource, setLoadingResource] = useState(false);
  const [showLock, setShowLock] = useState(false);

  const RESOURCE_TYPE_LABELS = {
    onboarding: { en: 'Onboarding', fr: 'Integration', icon: Users },
    parent_communication: { en: 'Parent Communication', fr: 'Communication aux parents', icon: FileText },
    certificate: { en: 'Certificates', fr: 'Certificats', icon: GraduationCap },
    assessment: { en: 'Assessment', fr: 'Evaluation', icon: BarChart3 },
    event_guide: { en: 'Events & Activities', fr: 'Evenements et activites', icon: BookOpen },
    cultural_enrichment: { en: 'Cultural Enrichment', fr: 'Enrichissement culturel', icon: BookOpen },
    curriculum_alignment: { en: 'Curriculum Alignment', fr: 'Alignement curriculaire', icon: FileText },
    substitute_guide: { en: 'Substitute Guides', fr: 'Guides de suppleance', icon: Users },
    iep_accommodation: { en: 'IEP & Accommodations', fr: 'PEI et accommodements', icon: Users },
    report_card: { en: 'Report Cards', fr: 'Bulletins', icon: FileText },
    procurement: { en: 'Procurement', fr: 'Approvisionnement', icon: FileText },
    pacing_guide: { en: 'Pacing Guides', fr: 'Guides de rythme', icon: Clock },
    session_plan: { en: 'Session Plans', fr: 'Plans de seances', icon: Clock },
  };

  const openResource = useCallback(async (res) => {
    setSelectedResource(res);
    setLoadingResource(true);
    try {
      const r = await axios.get(`${API}/programs/${program.program_id}/resources/${res.filename}`);
      setResourceContent(r.data);
    } catch {
      setResourceContent('Failed to load resource.');
    }
    setLoadingResource(false);
  }, [program.program_id]);

  const closeResource = () => {
    setSelectedResource(null);
    setResourceContent('');
  };

  // Group resources by type. Procurement kits are hidden from demo/customer-facing views.
  const grouped = {};
  for (const res of resources) {
    const type = res.type || 'other';
    if (type === 'procurement') continue;
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(res);
  }

  return (
    <div data-testid="program-resources">
      {Object.entries(grouped).map(([type, items]) => {
        const meta = RESOURCE_TYPE_LABELS[type] || { en: type, fr: type, icon: FileText };
        const TypeIcon = meta.icon;
        return (
          <div key={type} style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.5rem' }}>
              <TypeIcon size={16} color="#64748B" />
              <h3 style={{
                fontFamily: 'Outfit, sans-serif', fontSize: '0.92rem', fontWeight: 600,
                color: '#475569', margin: 0,
              }}>
                {isFr ? meta.fr : meta.en}
              </h3>
            </div>
            {items.map((res, idx) => (
              <button
                key={idx}
                data-testid={`resource-item-${res.resource_id || idx}`}
                onClick={() => (locked ? setShowLock(true) : openResource(res))}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.6rem 0.75rem', borderRadius: 8,
                  border: '1px solid #E2E8F0', marginBottom: '0.4rem',
                  textDecoration: 'none', color: '#1A1A2E', width: '100%',
                  background: 'white', cursor: 'pointer',
                  fontFamily: 'Nunito, sans-serif', textAlign: 'left',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
              >
                <FileText size={16} color="#94A3B8" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                    {isFr ? (res.title_fr || res.title) : res.title}
                  </div>
                </div>
                {locked
                  ? <Lock size={14} color="#94A3B8" />
                  : <BookOpen size={14} color="#94A3B8" />}
              </button>
            ))}
          </div>
        );
      })}
      {resources.length === 0 && (
        <p style={{ color: '#94A3B8', fontSize: '0.9rem', textAlign: 'center', padding: '2rem' }}>
          {isFr ? 'Aucune ressource disponible.' : 'No resources available.'}
        </p>
      )}

      {/* Resource Viewer Panel */}
      {selectedResource && (
        <div
          data-testid="resource-viewer-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeResource(); }}
        >
          <div
            data-testid="resource-viewer-panel"
            style={{
              width: 'min(720px, 90vw)', height: '100%',
              background: 'white', overflow: 'auto',
              boxShadow: '-8px 0 30px rgba(0,0,0,0.15)',
            }}
          >
            {/* Panel Header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '1rem 1.5rem', borderBottom: '1px solid #E2E8F0',
              position: 'sticky', top: 0, background: 'white', zIndex: 2,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{
                  fontFamily: 'Outfit, sans-serif', fontSize: '1.1rem', fontWeight: 700,
                  color: '#1A1A2E', margin: 0, lineHeight: 1.3,
                }}>
                  {isFr ? (selectedResource.title_fr || selectedResource.title) : selectedResource.title}
                </h2>
                {selectedResource.audience && (
                  <span style={{ fontSize: '0.78rem', color: '#94A3B8' }}>
                    {selectedResource.audience}
                  </span>
                )}
              </div>
              <a
                href={`${API}/programs/${program.program_id}/resources/${selectedResource.filename}`}
                download={selectedResource.filename}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 8,
                  border: '1px solid #E2E8F0', background: 'white',
                  color: '#475569', fontSize: '0.82rem', fontWeight: 600,
                  textDecoration: 'none', cursor: 'pointer',
                }}
              >
                <Download size={14} /> {isFr ? 'Source' : 'Raw'}
              </a>
              <button
                data-testid="resource-viewer-close"
                onClick={closeResource}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 34, height: 34, borderRadius: 8,
                  border: '1px solid #E2E8F0', background: 'white',
                  cursor: 'pointer', color: '#64748B',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Panel Content */}
            <div style={{ padding: '1.5rem' }}>
              {loadingResource ? (
                <p style={{ color: '#94A3B8', textAlign: 'center', padding: '2rem' }}>
                  {isFr ? 'Chargement...' : 'Loading...'}
                </p>
              ) : (
                <div className="resource-markdown" style={{
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.92rem',
                  color: '#1A1A2E', lineHeight: 1.7,
                }}>
                  <ReactMarkdown>{resourceContent}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Demo gate — documents open only on a fully provisioned account */}
      {showLock && (
        <div
          data-testid="resource-locked-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowLock(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(15,23,42,0.55)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
          }}
        >
          <div data-testid="resource-locked-dialog" style={{
            width: 'min(420px, 92vw)', background: 'white', borderRadius: 16,
            padding: '2rem 1.75rem', textAlign: 'center',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)', fontFamily: 'Nunito, sans-serif',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 28, margin: '0 auto 1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#EEF2FF',
            }}>
              <Lock size={26} color="#4C6EF5" />
            </div>
            <h3 style={{
              fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', fontWeight: 700,
              color: '#1A1A2E', margin: '0 0 0.5rem',
            }}>
              {isFr ? 'Réservé aux comptes complets' : 'Available on a fully provisioned account'}
            </h3>
            <p style={{ fontSize: '0.92rem', color: '#475569', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
              {isFr
                ? "Les ressources s'ouvrent uniquement sur un compte complet. Ceci est une démonstration."
                : 'Resources open only on a fully provisioned account. This is a demo.'}
            </p>
            <button
              data-testid="resource-locked-close"
              onClick={() => setShowLock(false)}
              style={{
                width: '100%', padding: '0.7rem 1rem', borderRadius: 10,
                border: 'none', background: '#4C6EF5', color: 'white',
                fontFamily: 'Outfit, sans-serif', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {isFr ? 'Compris' : 'Got it'}
            </button>
          </div>
        </div>
      )}

      {/* Markdown styles */}
      <style>{`
        .resource-markdown h1 { font-family: Outfit, sans-serif; font-size: 1.5rem; font-weight: 800; color: #1A1A2E; margin: 1.5rem 0 0.75rem; border-bottom: 2px solid #E2E8F0; padding-bottom: 0.5rem; }
        .resource-markdown h2 { font-family: Outfit, sans-serif; font-size: 1.2rem; font-weight: 700; color: #334155; margin: 1.25rem 0 0.5rem; }
        .resource-markdown h3 { font-family: Outfit, sans-serif; font-size: 1.05rem; font-weight: 600; color: #475569; margin: 1rem 0 0.4rem; }
        .resource-markdown p { margin: 0.5rem 0; }
        .resource-markdown ul, .resource-markdown ol { margin: 0.5rem 0; padding-left: 1.5rem; }
        .resource-markdown li { margin: 0.25rem 0; }
        .resource-markdown strong { color: #0F172A; }
        .resource-markdown blockquote { border-left: 3px solid #CBD5E1; margin: 0.75rem 0; padding: 0.5rem 1rem; background: #F8FAFC; border-radius: 0 8px 8px 0; color: #475569; }
        .resource-markdown code { background: #F1F5F9; padding: 2px 6px; border-radius: 4px; font-size: 0.88em; }
        .resource-markdown pre { background: #F1F5F9; padding: 1rem; border-radius: 8px; overflow-x: auto; }
        .resource-markdown table { width: 100%; border-collapse: collapse; margin: 0.75rem 0; }
        .resource-markdown th, .resource-markdown td { border: 1px solid #E2E8F0; padding: 0.5rem 0.75rem; text-align: left; font-size: 0.88rem; }
        .resource-markdown th { background: #F8FAFC; font-weight: 700; }
        .resource-markdown hr { border: none; border-top: 1px solid #E2E8F0; margin: 1.25rem 0; }
      `}</style>
    </div>
  );
}
